import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import {
  PlusOutlined,
  DeleteOutlined,
  SettingOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";
import { Skeleton, Space, Button, Tooltip, Dropdown } from "antd";
import "./CampaignPage.scss";
import CampaignModal from "../../components/CampaignComp/CampaignModal/CampaignModal";
import { ReactComponent as EditIcon } from "../../assets/Icons/Editicon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCampaigns,
  fetchAddCampaigns,
  fetchEditCampaigns,
} from "../../store/campaignSlice";
import { fetchUserFilters } from "../../store/filters";
import { formatCurrency } from "../../assets/helpers";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import {
  AddCampaigns,
  changeStatus,
  deleteCampaign,
  EditCampaigns,
} from "../../assets/api/campaignService";
import DeleteCampaignModal from "../../components/CampaignComp/DeleteCampaignModal/DeleteCampaignModal";

const EMPTY = "—";

const StaticSkeleton = () => <Skeleton.Input size="small" active />;

const TextOrDash = ({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (text === null || text === undefined || text === "") {
    return <span className="campaignEmpty">{EMPTY}</span>;
  }
  return <span className="campaignText">{text}</span>;
};

// Memoized cell components to prevent unnecessary re-renders
const MemoizedTextCell = memo(({ text, loading }) => (
  <TextOrDash text={text} loading={loading} />
));
MemoizedTextCell.displayName = "MemoizedTextCell";

const NameCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="campaignEmpty">{EMPTY}</span>;
  return <span className="campaignName">{text}</span>;
});
NameCell.displayName = "NameCell";

/* Figures are set in tabular numerals so a column of them lines up. */
const NumberCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (text === null || text === undefined || text === "") {
    return <span className="campaignEmpty">{EMPTY}</span>;
  }
  return <span className="campaignNumber">{text}</span>;
});
NumberCell.displayName = "NumberCell";

const POSCell = memo(({ text, loading, format }) => {
  if (loading) return <StaticSkeleton />;
  const value = formatCurrency(text, format);
  if (!value) return <span className="campaignEmpty">{EMPTY}</span>;
  return <span className="campaignNumber">{value}</span>;
});
POSCell.displayName = "POSCell";

const TypeCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="campaignEmpty">{EMPTY}</span>;
  return (
    <span className="campaignType">
      {text.charAt(0).toUpperCase() + text.slice(1)}
    </span>
  );
});
TypeCell.displayName = "TypeCell";

/* A campaign moves through start / pause / stop / resume, so the chip is
   banded by what the status means rather than being one flat colour. Each band
   also gets its own glyph, so the state still reads without the colour. */
const STATUS_BANDS = {
  active: { className: "is-active", hint: "Running — calls are being placed" },
  paused: { className: "is-paused", hint: "Paused — resume to continue calling" },
  stopped: { className: "is-stopped", hint: "Stopped — no calls are being placed" },
  done: { className: "is-done", hint: "Completed — the campaign has ended" },
  draft: { className: "is-draft", hint: "Not started — start it from Campaign Control" },
};

const statusBand = (status) => {
  switch (String(status || "").toLowerCase().replace(/[\s-]+/g, "_")) {
    case "active":
    case "running":
    case "started":
    case "resumed":
    case "live":
    case "in_progress":
    case "ongoing":
      return STATUS_BANDS.active;
    case "paused":
    case "on_hold":
      return STATUS_BANDS.paused;
    case "stopped":
    case "inactive":
    case "cancelled":
    case "canceled":
      return STATUS_BANDS.stopped;
    case "completed":
    case "finished":
    case "ended":
      return STATUS_BANDS.done;
    default:
      return STATUS_BANDS.draft;
  }
};

/* "in_progress" comes through as-is from the API; it reads as "In progress". */
const statusLabel = (status) => {
  const words = String(status).replace(/[_-]+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const StatusCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="campaignEmpty">{EMPTY}</span>;
  const band = statusBand(text);
  return (
    <Tooltip title={band.hint}>
      <span className={`campaignStatus ${band.className}`}>
        <i className="campaignStatus__glyph" aria-hidden="true" />
        {statusLabel(text)}
      </span>
    </Tooltip>
  );
});
StatusCell.displayName = "StatusCell";

/* A long agent list used to run as one comma-joined string that stretched the
   column. Two chips are shown and the rest collapse into a +N with the full
   list on hover, the way the multi-selects elsewhere report overflow. */
const AssignedAgentsCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;

  if (Array.isArray(text)) {
    if (text.length === 0) return <span className="campaignEmpty">{EMPTY}</span>;
    const names = text.map((a) => a?.name).filter(Boolean);
    if (names.length === 0) return <span className="campaignEmpty">{EMPTY}</span>;
    const shown = names.slice(0, 2);
    const rest = names.slice(2);
    return (
      <span className="campaignAgents">
        {shown.map((name) => (
          <span className="campaignAgents__chip" key={name}>
            {name}
          </span>
        ))}
        {rest.length > 0 && (
          <Tooltip title={rest.join(", ")}>
            <span className="campaignAgents__more">+{rest.length}</span>
          </Tooltip>
        )}
      </span>
    );
  }

  if (!text) return <span className="campaignEmpty">{EMPTY}</span>;
  return <span className="campaignText">{text}</span>;
});
AssignedAgentsCell.displayName = "AssignedAgentsCell";

// // Static options declared outside component to avoid recreation
// const typeOptions = [
//   { value: "progressive", label: "Progressive" },
//   { value: "predictive", label: "Predictive" },
// ];

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
];

const CampaignPage = () => {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deleteCampaignId, setDeleteCampaignId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadSubmit, setLoadSubmit] = useState(false);

  const { campaigns_list, campaign_list_loading } = useSelector(
    (state) => state.campaign,
  );
  const { filter_list } = useSelector((state) => state.filters);
  const { format } = useSelector((state) => state.priceFormat);
  const loading = campaign_list_loading;
  const listData = campaigns_list?.data;

  const data = useMemo(() => {
    return Array.isArray(listData)
      ? listData
      : listData && Array.isArray(listData.results)
        ? listData.results
        : [];
  }, [listData]);

  const handleEditClick = useCallback((record) => {
    setEditingCampaign(record);
    setIsModalOpen(true);
    setIsEdit(true);
  }, []);

  const handleDeleteClick = useCallback((record) => {
    console.log("Delete clicked for:", record);
    setDeleteCampaignId(record?.id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleStatusChange = useCallback(async (id, status) => {
    const payload = {
      action: status
    };
    try {
      await changeStatus(id, payload, token, user?.is_tc);
      dispatch(fetchCampaigns({ page: currentPage, page_size: pageSize }));
      CustomNotification.show("Campaign status changed successfully", "success");
    } catch (err) {
      console.error("Error changing status:", err);
      CustomNotification.show("Error changing campaign status", "error");
    }
  }, [currentPage, pageSize, token, user?.is_tc, dispatch]);

  useEffect(() => {
    dispatch(fetchCampaigns({ page: currentPage, page_size: pageSize }));
  }, [dispatch, currentPage, pageSize]);

  // useEffect(() => {
  //   if (!user?.is_tc) {
  //     dispatch(fetchUserFilters());
  //   }
  // }, [dispatch, user?.is_tc]);

  // const dynamicDispositionOptions = useMemo(() => {
  //   const data = filter_list?.find((item) => item?.name === "disposition")?.filters || [];
  //   return data.map((d) => ({
  //     value: d.id || d,
  //     label: d.name || d.key || d,
  //   }));
  // }, [filter_list]);

  // const dynamicPortfoliosOptions = useMemo(() => {
  //   const data = filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
  //   return data.map((d) => ({
  //     value: d.id || d,
  //     label: d.name || d.key || d,
  //   }));
  // }, [filter_list]);

  // const dynamicAssignedAgentOptions = useMemo(() => {
  //   const data = filter_list?.find((item) => item?.name === "telecallers")?.filters || [];
  //   return data.map((d) => ({
  //     value: d.id || d,
  //     label: d.name || d.key || d,
  //   }));
  // }, [filter_list]);

  const getStatusMenuItems = useCallback((recordId) => [
    {
      key: "start",
      label: "Start",
      onClick: () => handleStatusChange(recordId, "start"),
    },
    {
      key: "pause",
      label: "Pause",
      onClick: () => handleStatusChange(recordId, "pause"),
    },
    {
      key: "stop",
      label: "Stop",
      onClick: () => handleStatusChange(recordId, "stop"),
    },
    {
      key: "resume",
      label: "Resume",
      onClick: () => handleStatusChange(recordId, "resume"),
    },
  ], [handleStatusChange]);

  const columns = useMemo(() => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <NameCell text={text} loading={loading} />,
    },
    {
      title: "POS",
      dataIndex: "pos_value",
      key: "pos_value",
      render: (text) => <POSCell text={text} loading={loading} format={format} />,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => <TypeCell text={text} loading={loading} />,
    },
    {
      title: "Leads Count",
      dataIndex: "leads_count",
      key: "leads_count",
      render: (text) => <NumberCell text={text} loading={loading} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => <StatusCell text={text} loading={loading} />,
    },
    ...(!user?.is_tc ? [{
      title: "Assigned Agents",
      dataIndex: "assigned_agent",
      key: "assigned_agent",
      render: (text) => <AssignedAgentsCell text={text} loading={loading} />,
    }] : []),
    {
      title: "Action",
      key: "action",
      align: "center",
      width: user?.is_tc ? 80 : 140,
      render: (_, record) => {
        if (loading) return <StaticSkeleton />;
        const isDisabled = user?.is_tc && record?.type?.toLowerCase() === "predictive";
        return (
          <Space size={8}>
            <Tooltip title="Campaign Control">
              <Dropdown
                menu={{ items: getStatusMenuItems(record?.id) }}
                trigger={["click"]}
                disabled={isDisabled}
              >
                <Button
                  className="campaignIconBtn is-control"
                  icon={<SettingOutlined />}
                  disabled={isDisabled}
                />
              </Dropdown>
            </Tooltip>
            {!user?.is_tc && (
              <>
                <Tooltip title="Edit campaign">
                  <Button
                    className="campaignIconBtn is-edit"
                    icon={<EditIcon />}
                    onClick={() => handleEditClick(record)}
                  />
                </Tooltip>
                <Tooltip title="Delete campaign">
                  <Button
                    className="campaignIconBtn is-delete"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteClick(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ], [loading, format, user?.is_tc, getStatusMenuItems, handleEditClick, handleDeleteClick]);

  const handleSaveCampaign = useCallback(async (values) => {
    setLoadSubmit(true);
    const payload = { ...values };

    if (payload.assigned_agent) {
      const agentArr = Array.isArray(payload.assigned_agent)
        ? payload.assigned_agent
        : [payload.assigned_agent];
      payload.assigned_agent = agentArr.map((id) => Number(id));
    }

    if (isEdit) {
      delete payload.all_cases;
      delete payload.disposition;
    } else {
      if (payload.all_cases) {
        delete payload.disposition;
        payload.all_cases = true;
      } else {
        payload.all_cases = false;
        if (!payload.disposition || payload.disposition.length === 0) {
          delete payload.disposition;
        } else {
          const dispArr = Array.isArray(payload.disposition)
            ? payload.disposition
            : [payload.disposition];
          payload.disposition = dispArr.map((id) => Number(id));
        }
      }
    }

    if (payload.priority_calling) {
      // Keep priority_calling and priority_calling_order in the payload
    } else {
      delete payload.priority_calling;
      delete payload.priority_calling_order;
    }

    // Map checkbox object properties to retry_on lowercase list
    const retryOnMapping = {
      "All Calls": "all_calls",
      "Both Answered": "both_answered",
      "Cust. Ans": "cust_ans",
      "Agent Unanswered": "agent_unanswered",
      "Both Unanswered": "both_unanswered",
      "Rejected Call": "rejected_call",
      "Agent Failed.": "agent_failed",
      "Cust. Failed": "cust_failed",
      "Agent Busy": "agent_busy",
      "Cust. Unans - Agent Busy": "cust_unans_agent_busy",
      "Cust. Ans. - Agent Unans.": "cust_ans_agent_unans",
      "Cust. Unans - Agent Ans.": "cust_unans_agent_ans",
      "Cust. Unans.": "cust_unans",
      "Agent Ans.": "agent_ans",
      "Skipped": "skipped",
      "Cust. Failed - Agent Ans.": "cust_failed_agent_ans",
      "Cust. Ans. - Agent Failed": "cust_ans_agent_failed",
      "Cust. Ans. - Agent Not Found": "cust_ans_agent_not_found",
      "Cust. Hangup": "cust_hangup"
    };

    if (payload.retry_on && typeof payload.retry_on === "object" && !Array.isArray(payload.retry_on)) {
      const activeRetries = [];
      Object.keys(payload.retry_on).forEach(key => {
        if (payload.retry_on[key]) {
          const mappedValue = retryOnMapping[key] || key.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
          activeRetries.push(mappedValue);
        }
      });
      payload.retry_on = activeRetries;
    }

    // Map retries list to retry_steps
    if (payload.retries && Array.isArray(payload.retries)) {
      payload.retry_steps = payload.retries.map((step, index) => ({
        attempt: index + 1,
        gap_minutes: step.gap ? Number(step.gap) : 0,
        cli: step.cli ? String(step.cli) : undefined
      }));
    }
    delete payload.retries;

    // Map week days to lowercase
    if (payload.week_days && Array.isArray(payload.week_days)) {
      payload.week_days = payload.week_days.map(day => day.toLowerCase());
    }

    // Format Dates properly
    if (payload.retry_applicable_from && typeof payload.retry_applicable_from.format === "function") {
      payload.retry_applicable_from = payload.retry_applicable_from.format("YYYY-MM-DD");
    }
    if (payload.start_date) {
      payload.start_date = typeof payload.start_date.toISOString === "function"
        ? payload.start_date.toISOString()
        : payload.start_date;
    }
    if (payload.end_date && typeof payload.end_date.format === "function") {
      payload.end_date = payload.end_date.format("YYYY-MM-DD");
    }

    // Convert timing and counts to Numbers
    if (payload.working_hours_start_hour !== undefined) payload.working_hours_start_hour = Number(payload.working_hours_start_hour);
    if (payload.working_hours_start_minute !== undefined) payload.working_hours_start_minute = Number(payload.working_hours_start_minute);
    if (payload.working_hours_end_hour !== undefined) payload.working_hours_end_hour = Number(payload.working_hours_end_hour);
    if (payload.working_hours_end_minute !== undefined) payload.working_hours_end_minute = Number(payload.working_hours_end_minute);
    if (payload.retry_count !== undefined) payload.retry_count = Number(payload.retry_count);

    // Format boolean constraints and delete unused automatic retry fields when not needed
    payload.all_cases = !!payload.all_cases;
    payload.allow_recall_on_number = !!payload.allow_recall_on_number;

    if (!payload.allow_recall_on_number) {
      delete payload.automatic_retry_enabled;
      delete payload.retry_applicable_from;
      delete payload.retry_on;
      delete payload.retry_count;
      delete payload.retry_steps;
      delete payload.retry_same_agent_only;
      delete payload.retry_when_all_tries_done;
    } else {
      payload.automatic_retry_enabled = !!payload.automatic_retry_enabled;
      if (!payload.automatic_retry_enabled) {
        delete payload.retry_on;
        delete payload.retry_count;
        delete payload.retry_steps;
        delete payload.retry_same_agent_only;
        delete payload.retry_when_all_tries_done;
      } else {
        payload.retry_same_agent_only = !!payload.retry_same_agent_only;
        payload.retry_when_all_tries_done = !!payload.retry_when_all_tries_done;
      }
    }

    // Clean up empty date picker values if they are undefined or null
    if (!payload.retry_applicable_from) delete payload.retry_applicable_from;
    if (!payload.start_date) delete payload.start_date;
    if (!payload.end_date) delete payload.end_date;

    try {
      let res;
      if (isEdit && editingCampaign) {
        res = await EditCampaigns(payload, editingCampaign.id, token);
      } else {
        res = await AddCampaigns(payload, token);
      }

      if (res.status === 200 || res.status === 201) {
        CustomNotification.show(
          isEdit ? "Campaign updated successfully" : "Campaign added successfully",
          "success"
        );
        setIsModalOpen(false);
        setIsEdit(false);
        setEditingCampaign(null);
        dispatch(fetchCampaigns({ page: currentPage, page_size: pageSize }));
      }
    } catch (error) {
      console.error("API error details:", error);
      CustomNotification.show(
        error.message || (isEdit ? "Failed to update campaign" : "Failed to add campaign"),
        "error",
      );
    } finally {
      setLoadSubmit(false);
    }
  }, [token, currentPage, pageSize, dispatch, isEdit, editingCampaign]);

  const handleDeleteCampaign = useCallback(async () => {
    setLoadSubmit(true);
    try {
      const res = await deleteCampaign(deleteCampaignId, token);
      if (res.status === 200 || res.status === 201) {
        CustomNotification.show("Campaign deleted successfully", "success");
        setIsDeleteModalOpen(false);
        setDeleteCampaignId(null);
        dispatch(fetchCampaigns({ page: currentPage, page_size: pageSize }));
      }
    } catch (error) {
      CustomNotification.show(
        error.message || "Failed to delete campaign",
        "error",
      );
    } finally {
      setLoadSubmit(false);
    }
  }, [deleteCampaignId, token, currentPage, pageSize, dispatch]);

  const handleAddCampaignClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setIsEdit(false);
    setEditingCampaign(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteCampaignId(null);
  }, []);

  const handleTableChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const skeletonData = useMemo(() =>
    Array(5).fill({}).map((_, i) => ({ id: `skeleton-${i}` })),
    []
  );

  const totalItems = useMemo(() =>
    campaigns_list?.count || data?.length,
    [campaigns_list, data]
  );

  if (isModalOpen) {
    return (
      <CampaignModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveCampaign}
        load={loadSubmit}
        // dispositionOptions={dynamicDispositionOptions}
        // portfolioOptions={dynamicPortfoliosOptions}
        // assignedAgentOptions={dynamicAssignedAgentOptions}
        // typeOptions={typeOptions}
        isEdit={isEdit}
        initialData={editingCampaign}
      />
    );
  }

  return (
    <div className="campaignPageMainWrapper">
      {isDeleteModalOpen && <DeleteCampaignModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onDelete={handleDeleteCampaign}
        loading={loadSubmit}
      />}

      {/* Flush body + an inner `*-report-table` wrapper is how the dashboard
          panels carry a table, so the rows sit at the same inset here. */}
      <PanelCard
        title="All Campaigns"
        icon={<RocketOutlined />}
        accent="brand"
        padded={false}
        actions={
          !user?.is_tc ? (
            <Button
              className="addCampaignBtn"
              icon={<PlusOutlined />}
              onClick={handleAddCampaignClick}
            >
              Add Campaign
            </Button>
          ) : null
        }
      >
        <div className="campaign-report-table">
          <ReusableTable
            columns={columns}
            dataSource={loading ? skeletonData : data}
            totalItems={totalItems}
            rowKey="id"
            CurrentPageNo={currentPage}
            pageSize={pageSize}
            triggerChange={handleTableChange}
          />
        </div>
      </PanelCard>
    </div>
  );
};

export default memo(CampaignPage);
